import api from '../../../services/api';

export interface OrderStatusChangePayload {
  status: string;
  notes?: string;
  /** Used only when the change starts a confirmation cycle (decision 2026-10-04). */
  confirmationNotice?: { customer: boolean; operationsUserIds: string[]; adminUserIds: string[] };
  /** „Skąd realizujemy?” of the confirmation dialog (owner 2026-10-05): information for Logistyk (Valentyna). */
  fulfillmentSource?: 'WAREHOUSE' | 'FACTORY_DIRECT';
  fulfillmentNote?: string;
  /** „Potwierdź dostarczenie” (F6): delivery day `YYYY-MM-DD`, stored only with DELIVERED. */
  actualDelivery?: string;
}

export interface OrderStatusChangeResult {
  operationalWarning?: string | null;
}

export async function changeOrderStatus(
  orderId: string,
  payload: OrderStatusChangePayload,
): Promise<OrderStatusChangeResult> {
  const response = await api.patch(`/orders/${orderId}/status`, payload);
  return response.data;
}

export async function sendCustomerConfirmationEmail(
  orderId: string,
  options: { language?: 'pl' | 'en'; subject?: string; message?: string; html?: string } = {}
): Promise<{ success: boolean; recipient: string }> {
  const response = await api.post(`/orders/${orderId}/send-customer-confirmation`, options);
  return response.data;
}

export interface CustomerConfirmationPreview {
  subject: string;
  html: string;
  message: string;
  recipient: string | null;
  customerName: string | null;
  customerConfirmation?: {
    canSend: boolean;
    disabledReason: string | null;
  };
}

export async function previewCustomerConfirmationEmail(
  orderId: string,
  language: 'pl' | 'en' = 'pl'
): Promise<CustomerConfirmationPreview> {
  const response = await api.get(`/orders/${orderId}/customer-confirmation-preview`, {
    params: { language }
  });
  return response.data;
}

export async function previewCustomerConfirmationEmailDraft(
  orderId: string,
  payload: { language?: 'pl' | 'en'; subject?: string; message?: string }
): Promise<CustomerConfirmationPreview> {
  const response = await api.post(`/orders/${orderId}/customer-confirmation-preview`, payload);
  return response.data;
}

export interface CustomerStatusEmailPreview {
  subject: string;
  html: string;
  message: string;
  recipient: string | null;
  customerName: string | null;
  customerStatusEmail?: {
    canSend: boolean;
    disabledReason: string | null;
  };
}

export async function previewCustomerStatusEmail(
  orderId: string
): Promise<CustomerStatusEmailPreview> {
  const response = await api.get(`/orders/${orderId}/customer-status-email-preview`);
  return response.data;
}

export async function previewCustomerStatusEmailDraft(
  orderId: string,
  payload: { subject?: string; message?: string }
): Promise<CustomerStatusEmailPreview> {
  const response = await api.post(`/orders/${orderId}/customer-status-email-preview`, payload);
  return response.data;
}

export async function sendCustomerStatusEmail(
  orderId: string,
  payload: { subject?: string; message?: string; html?: string } = {}
): Promise<{ success: boolean; recipient: string }> {
  const response = await api.post(`/orders/${orderId}/send-customer-status-email`, payload);
  return response.data;
}

/**
 * ADMIN closes a ZK invoiced only in part (Emil, 2026-10-19): status
 * „Zamknięte fakturą” with the reason in the status history.
 */
export async function closeOrderManually(orderId: string, reason: string): Promise<void> {
  await api.post(`/orders/${orderId}/close-manually`, { reason });
}

/** Stable backend codes of the manual closure → Polish copy keys (orders namespace). */
export const ORDER_MANUAL_CLOSE_ERROR_KEYS: Record<string, string> = {
  ORDER_ALREADY_CLOSED: 'orders:manualClose.errors.alreadyClosed',
  ORDER_MANUAL_CLOSE_NO_INVOICE: 'orders:manualClose.errors.noInvoice',
  ORDER_MANUAL_CLOSE_CANCELLED: 'orders:manualClose.errors.cancelled',
  ORDER_MANUAL_CLOSE_REASON_REQUIRED: 'orders:manualClose.errors.reasonRequired',
};
